import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Storage, ref, getMetadata } from '@angular/fire/storage';
import { NgDialogModule } from '@principle-theorem/ng-shared';
import {
  findDuplicateMedia,
  getPatientMediaWithHashes,
} from '@principle-theorem/principle-core';
import {
  type IMedia,
  type IPatient,
} from '@principle-theorem/principle-core/interfaces';
import { type WithRef, Firestore } from '@principle-theorem/shared';

export interface IDeduplicatePatientMediaDialogData {
  patient: WithRef<IPatient>;
}

interface IDedupGroupItem {
  hash: string;
  keepName: string;
  duplicateNames: string[];
  duplicateCount: number;
  duplicates: WithRef<IMedia>[];
}

@Component({
  selector: 'pt-deduplicate-patient-media-dialog',
  templateUrl: './deduplicate-patient-media-dialog.component.html',
  styleUrls: ['./deduplicate-patient-media-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgDialogModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatIconModule,
  ],
})
export class DeduplicatePatientMediaDialogComponent {
  private _dialogRef =
    inject<MatDialogRef<DeduplicatePatientMediaDialogComponent>>(MatDialogRef);
  private _snackBar = inject(MatSnackBar);
  private _storage = inject(Storage);
  data = inject<IDeduplicatePatientMediaDialogData>(MAT_DIALOG_DATA);

  loading = signal(true);
  deleting = signal(false);
  dedupGroups = signal<IDedupGroupItem[]>([]);

  constructor() {
    void this._loadDuplicates();
  }

  async deleteSelected(): Promise<void> {
    const groups = this.dedupGroups();
    const allDuplicates = groups.flatMap((group) => group.duplicates);
    if (!allDuplicates.length) {
      return;
    }

    this.deleting.set(true);
    try {
      await Promise.all(
        allDuplicates.map((duplicate) => Firestore.deleteDoc(duplicate.ref))
      );
      const count = allDuplicates.length;
      this._snackBar.open(
        `Removed ${count} duplicate file${count === 1 ? '' : 's'}`,
        'OK',
        { duration: 3000 }
      );
      this._dialogRef.close(true);
    } catch {
      this._snackBar.open('Failed to remove duplicate media', 'OK', {
        duration: 5000,
      });
      this.deleting.set(false);
    }
  }

  private async _loadDuplicates(): Promise<void> {
    try {
      const resolveMd5 = async (path: string): Promise<string | undefined> => {
        try {
          const storageRef = ref(this._storage, path);
          const metadata = await getMetadata(storageRef);
          return metadata.md5Hash ?? undefined;
        } catch {
          return undefined;
        }
      };

      const mediaWithHashes = await getPatientMediaWithHashes(
        this.data.patient,
        resolveMd5
      );
      const groups = findDuplicateMedia(mediaWithHashes);

      this.dedupGroups.set(
        groups.map((group) => ({
          hash: group.hash,
          keepName: group.keep.name,
          duplicateNames: group.duplicates.map((duplicate) => duplicate.name),
          duplicateCount: group.duplicates.length,
          duplicates: group.duplicates,
        }))
      );
    } catch {
      this._snackBar.open('Failed to load duplicate media', 'OK', {
        duration: 5000,
      });
    } finally {
      this.loading.set(false);
    }
  }
}
